import { colorToString } from 'components/Editor/helper/color';
import _ from 'lodash';
import cx from 'classnames';
import { useState, useEffect, useRef } from 'react'; 

import styles from './background.module.scss';
import { IKImage } from 'imagekitio-react';
import { POSITION, SCALE } from 'constants/background';
import { getColorsConfig } from 'services/cssHelper.service';

const imageCDNUrl = process.env.REACT_APP_IMAGE_CDN;

export const BackgroundView = ({ className, background, style, partialSelection}) => {
  if (background == null) {
    return false;
  }

  const type = _.get(background, 'media.type') || _.get(background, 'type');
  const color = _.get(background, 'color');
  const blur = _.get(background, 'blur', 0);
  const colorOverlay = getColorsConfig(color);

  return (
    <div className={cx(styles.background, className, {[styles.partialSelection]: partialSelection})} style={style}>
      <div className={cx(styles.overlay, styles.color)} style={{
        background: colorOverlay,
        backdropFilter: `blur(${blur}px)`
      }}></div>

      {type === 'image' &&
        <ImageView background={background} />
      }

      {type === 'video' &&
        <VideoView background={background} />
      }
    </div>
  );
}

const ImageView = ({ background }) => {
  const media = _.get(background, 'media');
  const widgetRef = useRef(null);
  const [width, setWidth] = useState(0); 
  const [debouncedWidth, setDebouncedWidth] = useState(0);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);

  useEffect(() => {
    if (!widgetRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(widgetRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedWidth(width);
    }, 200);

    return () => clearTimeout(handler);
  }, [width]);

  useEffect(() => {
    if (!media) return;

    const newUrl = getImageUrl(background, debouncedWidth);

    if (newUrl === loadedUrl) return;

    setPendingUrl(newUrl);

    const img = new Image();
    img.src = newUrl;
    img.onload = () => {
      setLoadedUrl(newUrl);
      setPendingUrl(null);
    };
  }, [debouncedWidth, background, media]);

  if (!media) {
    return null;
  }

  const backgroundStyles = getImageBackgroundStyles(background, loadedUrl || pendingUrl);

  return (
    <div className={cx(styles.overlay, styles.image, 'background_poster')} style={backgroundStyles} ref={widgetRef}/>
    
  );
}

export const VideoView = ({ background }) => {
  const media = _.get(background, 'media');
  if (!media) {
    return null;
  }

  var url = `${imageCDNUrl}/${media.url}`;
  const backgroundStyles = getVideoBackgroundStyles(background);

  return (
    <div className={cx(styles.overlay, styles.video)}>
      <video key={url} style={backgroundStyles} playsInline preload='auto' autoPlay loop muted crossOrigin='anonymous'> 
        <source src={url} />
      </video>
    </div>
  );
}

function getImageBackgroundStyles(background, url) {
  var backgroundStyles = {};

  var scale = _.get(background, 'scale', SCALE.SCALE_TO_FILL);
  var opacity = _.get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    backgroundImage: `url('${url}')`,
    opacity: opacity
  }

  switch (scale) {
    case SCALE.TILED:
      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto'
      }

      break;
    case SCALE.SCALE_TO_FILL:
    default:

      backgroundStyles = {
        ...backgroundStyles,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'     
      }
      break;
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    backgroundPosition: backgroundPosition
  }

  return backgroundStyles;
}

function getVideoBackgroundStyles(background) {
  var backgroundStyles = {};

  var opacity = _.get(background, 'opacity', 100) / 100;

  backgroundStyles = {
    ...backgroundStyles,
    opacity: opacity
  }

  const backgroundPosition = getBackgroundPosition(background);
  backgroundStyles = {
    ...backgroundStyles,
    objectPosition: backgroundPosition,
    width: '100%'
  }

  return backgroundStyles;
}

function getBackgroundPosition(background) {
  var position = _.get(background, 'position', POSITION.CENTER);

  var backgroundPosition = 'center center';
  switch (position) {
    case POSITION.TOP_LEFT:
      backgroundPosition = 'left top';
      break;

    case POSITION.TOP_CENTER:
      backgroundPosition = 'center top';
      break;

    case POSITION.TOP_RIGHT:
      backgroundPosition = 'right top';
      break;

    case POSITION.CENTER_LEFT:
      backgroundPosition = 'left center';
      break;

    case POSITION.CENTER:
      backgroundPosition = 'center center';
      break;

    case POSITION.CENTER_RIGHT:
      backgroundPosition = 'right center';
      break;

    case POSITION.BOTTOM_LEFT:
      backgroundPosition = 'left bottom';
      break;

    case POSITION.BOTTOM_CENTER:
      backgroundPosition = 'center bottom';
      break;

    case POSITION.BOTTOM_RIGHT:
      backgroundPosition = 'right bottom';
      break;
  }

  return backgroundPosition;
}

function getImageUrl(background, width) {
  const media = _.get(background, 'media');
  const scale = _.get(background, 'scale', SCALE.SCALE_TO_FILL);

  if (!media) return null;

  let baseUrl = `${imageCDNUrl}/${media.url}`;

  if (width > 0) {
    if(scale === SCALE.TILED) {
      baseUrl = `${imageCDNUrl}/tr:q-80/${media.url}`;
    } else {
      baseUrl = `${imageCDNUrl}/tr:w-${width * 2},q-80/${media.url}`;
    }
  }

  return baseUrl;
}

// export default BackgroundView;
